import React from "react";

import Divider from "@busbud/design-system-components/dist/Divider";
import Numerical from "@busbud/design-system-components/dist/Numerical";
import Stack from "@busbud/design-system-components/dist/Stack";
import Text from "@busbud/design-system-components/dist/Text";

import { LocalizedPrice } from "@app/components/localized-price";
import type { ErrorModalButtonType } from "@app/helpers/api-error";
import { useAppContext } from "@app/helpers/hooks";
import { Money } from "@app/lib/money";
import { DeparturePassengerSummary } from "@app/modules/checkout/components/price-information/departure-passenger-summary";
import { FeeList } from "@app/modules/checkout/components/price-information/fee-list";
import type { CartCharges } from "@app/modules/checkout/components/price-information/fee-list/fee-list";
import {
  cancelledPriceUpdate,
  clickedUpdatePrice
} from "@app/tracking/checkout-tracking";
import type {
  CheckoutFormField,
  CheckoutPassengerState
} from "@app/types/checkout-form-types";
import type { CurrencyCode } from "@app/types/currency";
import type { InvalidPriceAdditionalData } from "@app/types/napi-charge-types";
import type { NapiV2CartCharges } from "@app/types/napi-v2-charges-types";

import type { ConnectorProps } from "./checkout-price-changed.connect";

import { useStyles } from "./checkout-price-changed.styles";
import { AlertDialog } from "../../alert-dialog";

export interface RefreshedCartCharges {
  updated_total: number;
  currency: CurrencyCode;
  updated_charges: CartCharges;
  items?: NapiV2CartCharges["items"]; // Cart V2 exclusive, used in the reducer
}
export interface OwnProps {
  onAccept: () => void;
  onDecline: () => void;
  refreshed_cart_charges: InvalidPriceAdditionalData | RefreshedCartCharges;
  passenger_section_state: CheckoutPassengerState[];
  passenger_section_fields: CheckoutFormField[][];
}

interface Props extends OwnProps, ConnectorProps {}

export const CheckoutPriceChangedComponent: React.FC<Props> = props => {
  const { browser_locale, tracker, translator } = useAppContext();
  const {
    initial_discount_code,
    currency,
    refreshed_cart_charges,
    charges_total,
    operators_name,
    source_names
  } = props;
  const { classes } = useStyles();

  const _handleRefreshCartPriceAccepted = () => {
    _fireTrackingEvent(true);
    props.onAccept();
  };

  const _handleRefreshCartPriceDeclined = () => {
    _fireTrackingEvent(false);
    props.onDecline();
  };

  const getMessage = () => {
    const price_change = refreshed_cart_charges.updated_total - charges_total;

    const formatted_price = new Money(Math.abs(price_change)).getPrice(
      refreshed_cart_charges.currency,
      browser_locale
    );

    if (price_change > 0) {
      return translator.t("!checkout.invalid-price-modal.price-increase", {
        price_change: formatted_price
      });
    }

    return translator.t("!checkout.invalid-price-modal.price-decrease", {
      price_change: formatted_price
    });
  };

  const operator_names: string = operators_name.join(", ");

  const _fireTrackingEvent = (accepted: boolean) => {
    if (accepted) {
      tracker.track(
        clickedUpdatePrice(
          refreshed_cart_charges.updated_total,
          charges_total,
          operator_names,
          tracker.analytics
        )
      );
    } else {
      tracker.track(
        cancelledPriceUpdate(
          refreshed_cart_charges.updated_total,
          charges_total,
          operator_names,
          tracker.analytics
        )
      );
    }
  };

  const message = (
    <Stack direction="column" spacing={2} className={classes.content}>
      <Text size="lg" align="center" className={classes.textDivider}>
        {getMessage()}
      </Text>
      <DeparturePassengerSummary
        departures={props.departures}
        passenger_first_names={props.passenger_first_names}
        currency={currency}
      />
      <FeeList
        is_discount_supported={false}
        charges={refreshed_cart_charges.updated_charges}
        operators_name={operators_name}
        initial_discount_code={initial_discount_code}
        source_names={source_names}
      />
      <Divider />
      <Stack
        justifyContent="space-between"
        alignItems="center"
        className={classes.totalContainer}
      >
        <Text size="lg" fontWeight="bold">
          {translator.t("!checkout.summary.fees-taxes.total")}
        </Text>
        <Numerical size="lg" fontWeight="bold" data-cy="checkout-total-price">
          <LocalizedPrice
            ceiled
            without_cents
            price_in_cents={refreshed_cart_charges.updated_total}
          />
        </Numerical>
      </Stack>
    </Stack>
  );

  const onAction = (button_type: ErrorModalButtonType) =>
    button_type === "cta"
      ? _handleRefreshCartPriceAccepted()
      : _handleRefreshCartPriceDeclined();

  return (
    <AlertDialog
      title={translator.t("!checkout.invalid-price-modal.title")}
      message={message}
      actionHandler={onAction}
      data-cy="invalid-price-modal"
      cta_button={{
        label: translator.t("!checkout.invalid-price-modal.action-accept"),
        props: {
          "data-cy": "invalid-price-modal-confirm"
        }
      }}
      secondary_button={{
        label: translator.t("!checkout.invalid-price-modal.action-decline")
      }}
    />
  );
};
